import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  SafetyAction,
  ActionStatus,
  ActionPriority,
  SafetyActionExtension,
  SafetyActionVerification,
  SafetyActionEffectivenessReview,
  SafetyActionAssignment,
  SafetyActionComment,
  SafetyActionStatusHistory,
} from '../types';
import { actionService } from '../actionService';

interface ActionDetailModalProps {
  actionId: string;
  onClose: () => void;
  onUpdated: () => void;
}

type TabType = 'overview' | 'assignment' | 'verification' | 'effectiveness' | 'extensions' | 'history';

const ALL_STATUSES: ActionStatus[] = [
  'DRAFT',
  'ASSIGNED',
  'ACCEPTED',
  'IN_PROGRESS',
  'COMPLETED',
  'PENDING_VERIFICATION',
  'VERIFIED',
  'EFFECTIVENESS_REVIEW',
  'CLOSED',
  'CANCELLED',
];

export const ActionDetailModal: React.FC<ActionDetailModalProps> = ({
  actionId,
  onClose,
  onUpdated,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [action, setAction] = useState<SafetyAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Status transition state
  const [selectedToStatus, setSelectedToStatus] = useState<ActionStatus>('IN_PROGRESS');
  const [transitionReason, setTransitionReason] = useState('');

  // Reassignment state
  const [toAssigneeName, setToAssigneeName] = useState('');
  const [toDepartment, setToDepartment] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  // Completion state
  const [completionNotes, setCompletionNotes] = useState('');
  const [completedBy, setCompletedBy] = useState('Safety Coordinator');

  // Verification state (SoD)
  const [verifierName, setVerifierName] = useState('Independent Safety Auditor');
  const [verifierRole, setVerifierRole] = useState('Quality Assurance Inspector');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [isVerified, setIsVerified] = useState(true);

  // Effectiveness review state
  const [reviewerName, setReviewerName] = useState('Safety Manager');
  const [methodology, setMethodology] = useState('FLIGHT_DATA_MONITORING');
  const [isEffective, setIsEffective] = useState(true);
  const [residualImpact, setResidualImpact] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  // Extension request state
  const [extDueDate, setExtDueDate] = useState('');
  const [extReason, setExtReason] = useState('');

  // Comment state
  const [commentText, setCommentText] = useState('');

  const loadAction = async () => {
    try {
      setLoading(true);
      const data = await actionService.getActionById(actionId);
      setAction(data);
      if (data) {
        setSelectedToStatus(data.status);
      }
    } catch (err: any) {
      console.error('Failed to load action detail:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAction();
  }, [actionId]);

  const handleTransitionStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action) return;
    if (selectedToStatus === action.status) {
      alert('Selected status is the same as current status.');
      return;
    }
    if (!transitionReason.trim()) {
      alert('Please provide a reason or justification for status change.');
      return;
    }

    try {
      setActionLoading(true);
      await actionService.transitionStatus(action.id, selectedToStatus, transitionReason.trim(), 'SAFETY_OFFICER');
      setTransitionReason('');
      await loadAction();
      onUpdated();
      alert(`Status transitioned to ${selectedToStatus} successfully!`);
    } catch (err: any) {
      alert(`Transition failed: ${err.message || 'Error occurred'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action || !toAssigneeName.trim()) return;

    try {
      setActionLoading(true);
      const newAssigneeId = `USR-${Date.now().toString().slice(-4)}`;
      await actionService.assignAction(
        action.id,
        newAssigneeId,
        toAssigneeName.trim(),
        toDepartment.trim() || undefined,
        reassignReason.trim() || undefined,
        'SAFETY_COORDINATOR'
      );
      setToAssigneeName('');
      setReassignReason('');
      await loadAction();
      onUpdated();
      alert('Action reassigned successfully!');
    } catch (err: any) {
      alert(`Reassignment failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action || !completionNotes.trim()) {
      alert('Completion notes / evidence details are required.');
      return;
    }

    try {
      setActionLoading(true);
      await actionService.completeAction(action.id, completionNotes.trim(), completedBy.trim());
      setCompletionNotes('');
      await loadAction();
      onUpdated();
      alert('Safety action marked as COMPLETED! Pending independent verification.');
    } catch (err: any) {
      alert(`Completion failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action || !verificationNotes.trim()) {
      alert('Verification evidence notes are required.');
      return;
    }

    // Check SoD client side warning
    if (action.assigneeName && action.assigneeName.toLowerCase() === verifierName.toLowerCase()) {
      alert('Segregation of Duties Violation: Action assignee cannot perform verification!');
      return;
    }

    try {
      setActionLoading(true);
      await actionService.verifyAction(action.id, {
        verifierId: `USR-VER-${Date.now().toString().slice(-4)}`,
        verifierName: verifierName.trim(),
        isVerified,
        evidenceNotes: verificationNotes.trim(),
        signoffRole: verifierRole.trim(),
      });
      setVerificationNotes('');
      await loadAction();
      onUpdated();
      alert(isVerified ? 'Action verified successfully!' : 'Verification rejected. Action returned to IN_PROGRESS.');
    } catch (err: any) {
      alert(`Verification failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordEffectiveness = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action || !residualImpact.trim()) {
      alert('Residual impact explanation is required.');
      return;
    }

    try {
      setActionLoading(true);
      await actionService.recordEffectivenessReview(action.id, {
        reviewerId: 'USR-SAF-MGR',
        reviewerName: reviewerName.trim(),
        methodology,
        isEffective,
        residualImpact: residualImpact.trim(),
        notes: reviewNotes.trim() || undefined,
      });
      setResidualImpact('');
      setReviewNotes('');
      await loadAction();
      onUpdated();
      alert(isEffective ? 'Effectiveness confirmed! Action is now CLOSED.' : 'Action marked ineffective and reopened.');
    } catch (err: any) {
      alert(`Effectiveness review failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestExtension = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action || !extDueDate || !extReason.trim()) {
      alert('Requested due date and operational justification are required.');
      return;
    }

    try {
      setActionLoading(true);
      await actionService.requestExtension(
        action.id,
        new Date(extDueDate).toISOString(),
        extReason.trim(),
        'Action Owner'
      );
      setExtDueDate('');
      setExtReason('');
      await loadAction();
      onUpdated();
      alert('Due date extension request submitted for approval!');
    } catch (err: any) {
      alert(`Extension request failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecideExtension = async (extId: string, approved: boolean) => {
    if (!action) return;
    const notes = prompt(`Enter decision comments for ${approved ? 'APPROVAL' : 'REJECTION'}:`);
    if (notes === null) return;

    try {
      setActionLoading(true);
      await actionService.decideExtension(action.id, extId, approved, notes || 'Standard review', 'Safety Director');
      await loadAction();
      onUpdated();
      alert(`Extension request ${approved ? 'APPROVED' : 'REJECTED'} successfully!`);
    } catch (err: any) {
      alert(`Decision failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action || !commentText.trim()) return;

    try {
      setActionLoading(true);
      await actionService.addComment(action.id, commentText.trim(), 'USR-SAFETY', 'Safety Officer');
      setCommentText('');
      await loadAction();
    } catch (err: any) {
      alert(`Failed to add note: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !action) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div style={{ background: '#ffffff', padding: '24px', borderRadius: '8px', color: '#64748b' }}>
          Loading Safety Action Dossier...
        </div>
      </div>
    );
  }

  if (!action) return null;

  const isExtended = action.currentDueDate !== action.originalDueDate;

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#0284c7', fontFamily: 'monospace' }}>
                {action.actionNumber}
              </span>
              <span
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  border: '1px solid #cbd5e1',
                }}
              >
                {action.status}
              </span>
              <span
                style={{
                  background: action.priority === 'IMMEDIATE' ? '#fee2e2' : '#fef3c7',
                  color: action.priority === 'IMMEDIATE' ? '#b91c1c' : '#b45309',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 700,
                }}
              >
                {action.priority}
              </span>
            </div>
            <h2 style={{ fontSize: '19px', fontWeight: 700, margin: 0, color: '#0f172a' }}>
              {action.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            background: '#ffffff',
            padding: '0 24px',
            gap: '8px',
            overflowX: 'auto',
          }}
        >
          {[
            { id: 'overview', label: 'Overview & Lifecycle' },
            { id: 'assignment', label: `Assignments (${action.assignments?.length || 0})` },
            { id: 'verification', label: `Verification (${action.verifications?.length || 0})` },
            { id: 'effectiveness', label: `Effectiveness (${action.effectivenessReviews?.length || 0})` },
            { id: 'extensions', label: `Extensions (${action.extensions?.length || 0})` },
            { id: 'history', label: 'Audit Trail & Notes' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                padding: '12px 14px',
                border: 'none',
                background: 'transparent',
                fontSize: '13px',
                fontWeight: activeTab === tab.id ? 700 : 500,
                color: activeTab === tab.id ? '#0284c7' : '#64748b',
                borderBottom: activeTab === tab.id ? '2px solid #0284c7' : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Body */}
        <div style={{ overflowY: 'auto', padding: '24px', flex: 1 }}>
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Deadline Integrity Card */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                }}
              >
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Original Target Due Date
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>
                    {new Date(action.originalDueDate).toLocaleDateString()}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Locked historical baseline (Cannot be overwritten)
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '11px', color: isExtended ? '#b45309' : '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Current Due Date {isExtended ? '(Extended)' : ''}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: isExtended ? '#d97706' : '#0f172a', marginTop: '4px' }}>
                    {new Date(action.currentDueDate).toLocaleDateString()}
                  </div>
                  <div style={{ fontSize: '11px', color: isExtended ? '#b45309' : '#64748b', marginTop: '2px' }}>
                    {isExtended ? 'Modified under approved governance extension' : 'Matching baseline schedule'}
                  </div>
                </div>
              </div>

              {/* Status Transition Control */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#ffffff',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  Lifecycle Status Transition
                </h4>
                <form onSubmit={handleTransitionStatus} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: '220px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Target Status
                    </label>
                    <select
                      value={selectedToStatus}
                      onChange={(e) => setSelectedToStatus(e.target.value as ActionStatus)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                      }}
                    >
                      {ALL_STATUSES.map((st) => (
                        <option key={st} value={st}>
                          {st.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Reason / Justification *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Simulator trial passed, awaiting fleet manual revision"
                      value={transitionReason}
                      onChange={(e) => setTransitionReason(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ alignSelf: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading || selectedToStatus === action.status}
                      style={{
                        padding: '8px 16px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: actionLoading || selectedToStatus === action.status ? 'not-allowed' : 'pointer',
                        opacity: selectedToStatus === action.status ? 0.5 : 1,
                      }}
                    >
                      Apply Status Change
                    </button>
                  </div>
                </form>
              </div>

              {/* Action Attributes */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Action Type</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {action.actionType}
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Responsible Dept</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {action.department || 'General'}
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Source Context</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {action.sourceReference ? `${action.sourceModule}: ${action.sourceReference}` : 'Direct SMS Action'}
                  </div>
                </div>
              </div>

              {/* Description */}
              {action.description && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#334155', margin: '0 0 8px 0' }}>
                    Implementation Scope & Objectives
                  </h4>
                  <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {action.description}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: ASSIGNMENT & HANDOVER */}
          {activeTab === 'assignment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  Reassign Action / Department Handover
                </h4>
                <form onSubmit={handleReassign} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        New Assignee Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Captain Mehmet Yilmaz"
                        value={toAssigneeName}
                        onChange={(e) => setToAssigneeName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        New Department
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Flight Training Department"
                        value={toDepartment}
                        onChange={(e) => setToDepartment(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Handover Reason & Instructions
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Explain reason for handover, specific deliverables or milestones..."
                      value={reassignReason}
                      onChange={(e) => setReassignReason(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading || !toAssigneeName.trim()}
                      style={{
                        padding: '8px 18px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Execute Handover
                    </button>
                  </div>
                </form>
              </div>

              {/* Assignment History Table */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Handover & Assignment History ({action.assignments?.length || 0})
                </h4>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                        <th style={{ padding: '10px 14px' }}>Assigned Date</th>
                        <th style={{ padding: '10px 14px' }}>Previous Assignee</th>
                        <th style={{ padding: '10px 14px' }}>New Assignee</th>
                        <th style={{ padding: '10px 14px' }}>Department</th>
                        <th style={{ padding: '10px 14px' }}>Handover Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!action.assignments || action.assignments.length === 0) ? (
                        <tr>
                          <td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>
                            No reassignment handovers recorded.
                          </td>
                        </tr>
                      ) : (
                        action.assignments.map((asgn) => (
                          <tr key={asgn.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 14px', color: '#64748b' }}>
                              {new Date(asgn.assignedAt).toLocaleString()}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#475569' }}>
                              {asgn.fromAssigneeName || 'None'}
                            </td>
                            <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0284c7' }}>
                              {asgn.toAssigneeName}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#334155' }}>
                              {asgn.toDepartment || '-'}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#0f172a' }}>
                              {asgn.reassignmentReason || '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: COMPLETION & INDEPENDENT VERIFICATION */}
          {activeTab === 'verification' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Completion Section */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: action.completionDate ? '#f0fdf4' : '#ffffff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                    Step 1: Physical Completion by Assignee
                  </h4>
                  {action.completionDate && (
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>
                      COMPLETED ON {new Date(action.completionDate).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {action.completionDate ? (
                  <div style={{ fontSize: '13px', color: '#166534' }}>
                    <strong>Completion Statement:</strong> {action.completionNotes}
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                      Submitted by: {action.completedBy || action.assigneeName}
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleComplete} style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                    <textarea
                      rows={2}
                      required
                      placeholder="Detail physical completion evidence (e.g. document revisions published, work orders closed, sim sessions logged)..."
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="submit"
                        disabled={actionLoading}
                        style={{
                          padding: '8px 16px',
                          background: '#0284c7',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Declare Completed
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Independent Verification Form (Segregation of Duties) */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 4px 0' }}>
                    Step 2: Independent Verification (Segregation of Duties)
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    Under ICAO SMS rules, the person who executed the action <strong>CANNOT</strong> verify it. An independent safety verifier must validate proof of implementation.
                  </p>
                </div>

                <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Verifier Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={verifierName}
                        onChange={(e) => setVerifierName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Signoff Role / Capacity
                      </label>
                      <input
                        type="text"
                        required
                        value={verifierRole}
                        onChange={(e) => setVerifierRole(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Verification Verdict
                      </label>
                      <select
                        value={isVerified ? 'YES' : 'NO'}
                        onChange={(e) => setIsVerified(e.target.value === 'YES')}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          background: '#ffffff',
                        }}
                      >
                        <option value="YES">VERIFIED (Evidence Valid & Complete)</option>
                        <option value="NO">REJECTED (Insufficient Evidence / Incomplete)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Verification Notes & Findings *
                    </label>
                    <textarea
                      rows={2}
                      required
                      placeholder="Explain how evidence was audited, reviewed, or observed on site..."
                      value={verificationNotes}
                      onChange={(e) => setVerificationNotes(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      style={{
                        padding: '8px 18px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Record Verification Signoff
                    </button>
                  </div>
                </form>
              </div>

              {/* Past Verifications */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Verification Records ({action.verifications?.length || 0})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {action.verifications?.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: '#ffffff',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: v.isVerified ? '#15803d' : '#b91c1c',
                              background: v.isVerified ? '#dcfce7' : '#fee2e2',
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {v.isVerified ? 'VERIFIED' : 'REJECTED'}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                            {v.verifierName} ({v.signoffRole})
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {new Date(v.verificationDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#475569' }}>
                        {v.evidenceNotes}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: EFFECTIVENESS EVALUATION REVIEW */}
          {activeTab === 'effectiveness' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 4px 0' }}>
                    Effectiveness Evaluation (Verification ≠ Effectiveness)
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    Verification checks if the action was executed. Effectiveness evaluates if the safety action actually <strong>reduced operational risk</strong> in practice.
                  </p>
                </div>

                <form onSubmit={handleRecordEffectiveness} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Reviewer Authority *
                      </label>
                      <input
                        type="text"
                        required
                        value={reviewerName}
                        onChange={(e) => setReviewerName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Methodology
                      </label>
                      <select
                        value={methodology}
                        onChange={(e) => setMethodology(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          background: '#ffffff',
                        }}
                      >
                        <option value="FLIGHT_DATA_MONITORING">Flight Data Monitoring (FDM) Trends</option>
                        <option value="AUDIT">Follow-up Safety Audit</option>
                        <option value="INCIDENT_RATE">Incident / Occurrence Rate Metrics</option>
                        <option value="SURVEY">Safety Culture & Crew Survey</option>
                        <option value="INSPECTION">On-site Ramp / Line Inspection</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Verdict
                      </label>
                      <select
                        value={isEffective ? 'YES' : 'NO'}
                        onChange={(e) => setIsEffective(e.target.value === 'YES')}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          background: '#ffffff',
                        }}
                      >
                        <option value="YES">EFFECTIVE (Risk successfully mitigated)</option>
                        <option value="NO">INEFFECTIVE (Reopen for further mitigation)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Residual Impact & Operational Data Evidence *
                    </label>
                    <textarea
                      rows={2}
                      required
                      placeholder="e.g. Statistical data reveals zero unstabilized approaches over the last 60 flight cycles..."
                      value={residualImpact}
                      onChange={(e) => setResidualImpact(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      style={{
                        padding: '8px 18px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Record Effectiveness & Close Action
                    </button>
                  </div>
                </form>
              </div>

              {/* Past Effectiveness Reviews */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Effectiveness Reviews ({action.effectivenessReviews?.length || 0})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {action.effectivenessReviews?.map((r) => (
                    <div
                      key={r.id}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: '#ffffff',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: r.isEffective ? '#15803d' : '#b91c1c',
                              background: r.isEffective ? '#dcfce7' : '#fee2e2',
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {r.isEffective ? 'EFFECTIVE' : 'INEFFECTIVE'}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                            {r.reviewerName} ({r.methodology})
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {new Date(r.reviewDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#475569' }}>
                        {r.residualImpact}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: DUE DATE EXTENSIONS */}
          {activeTab === 'extensions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 4px 0' }}>
                    Request Due Date Extension (Deadline Integrity Rule)
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    Extensions require documented operational justification. Historical target dates are never overwritten.
                  </p>
                </div>

                <form onSubmit={handleRequestExtension} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Proposed Extended Due Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={extDueDate}
                        onChange={(e) => setExtDueDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Justification & Mitigating Controls During Extension *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Explain reason for delay and interim risk controls implemented..."
                        value={extReason}
                        onChange={(e) => setExtReason(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      style={{
                        padding: '8px 18px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Submit Extension Request
                    </button>
                  </div>
                </form>
              </div>

              {/* Extension Records List */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Extension Requests & Governance Records ({action.extensions?.length || 0})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {action.extensions?.map((ext) => (
                    <div
                      key={ext.id}
                      style={{
                        padding: '14px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color:
                                ext.approvalStatus === 'APPROVED'
                                  ? '#15803d'
                                  : ext.approvalStatus === 'REJECTED'
                                  ? '#b91c1c'
                                  : '#b45309',
                              background:
                                ext.approvalStatus === 'APPROVED'
                                  ? '#dcfce7'
                                  : ext.approvalStatus === 'REJECTED'
                                  ? '#fee2e2'
                                  : '#fef3c7',
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {ext.approvalStatus}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                            Requested Target: {new Date(ext.requestedDueDate).toLocaleDateString()}
                          </span>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            (Was: {new Date(ext.previousDueDate).toLocaleDateString()})
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#475569' }}>
                          <strong>Reason:</strong> {ext.reason}
                        </div>
                        {ext.decisionNotes && (
                          <div style={{ fontSize: '11px', color: '#0369a1', marginTop: '2px' }}>
                            Decision: {ext.decisionNotes} by {ext.approvedBy}
                          </div>
                        )}
                      </div>

                      {ext.approvalStatus === 'PENDING' && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => handleDecideExtension(ext.id, true)}
                            style={{
                              background: '#10b981',
                              color: '#ffffff',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDecideExtension(ext.id, false)}
                            style={{
                              background: '#ef4444',
                              color: '#ffffff',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: AUDIT TRAIL, EVIDENCE & NOTES */}
          {activeTab === 'history' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Comments / Notes */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Collaboration Notes
                </h4>
                <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="Add operational comment or coordination note..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!commentText.trim() || actionLoading}
                    style={{
                      padding: '8px 16px',
                      background: '#0284c7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Post Note
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {action.comments?.map((cm) => (
                    <div
                      key={cm.id}
                      style={{
                        padding: '10px 14px',
                        background: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>{cm.userName}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                          {new Date(cm.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#334155' }}>{cm.commentText}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status History Audit Table */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Status History & Audit Trail
                </h4>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                        <th style={{ padding: '10px 14px' }}>Timestamp</th>
                        <th style={{ padding: '10px 14px' }}>From</th>
                        <th style={{ padding: '10px 14px' }}>To</th>
                        <th style={{ padding: '10px 14px' }}>Reason</th>
                        <th style={{ padding: '10px 14px' }}>Actor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {action.statusHistory?.map((h) => (
                        <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', color: '#64748b' }}>
                            {new Date(h.changedAt).toLocaleString()}
                          </td>
                          <td style={{ padding: '10px 14px', color: '#475569' }}>{h.fromStatus}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0284c7' }}>
                            {h.toStatus}
                          </td>
                          <td style={{ padding: '10px 14px', color: '#0f172a' }}>{h.reason || '-'}</td>
                          <td style={{ padding: '10px 14px', color: '#64748b' }}>{h.changedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
